import React, { ReactElement } from "react";
import Grid from "@mui/material/Grid";
import ButtonLoading from "../Button/ButtonLoading";
import ApiClient from "../../services/ApiClient";
import { useSnackbar } from "notistack";
import useForm from "../../hooks/useForm";
import { formValue } from "../../services/formServiceFunctions";
import { getCaseLink } from "../../services/Product/ProductService/productService";
import ContentBox from "../ContentBox/ContentBox";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { scrollToAnchor } from "../../theme/commonStyles";
import { fetchGeneratableFiles } from "../../services/generatableFilesService";
import { AvailableGeneratableDocument } from "../../types/GeneratableFile";
import LegalbirdAutoComplete from "../AutoComplete/LegalbirdAutoComplete";
import { AutoCompleteOptionWithValue } from "../../types/AutoCompleteOptionWithValue";
import PdfGeneratorPreview from "../Case/CasePdfGenerator/PdfGeneratorPreview";
import { ProductUnion } from "../../types/ProductUnion";

type CaseFileGenerateSubmitButtonProps = {
  isLoading: boolean;
  product: ProductUnion;
  disabled: boolean;
};

type CaseFileGenerateFormConfig = {
  scrollToAnchor: string;
  title: string;
  postUrl: string;
  helperText: string;
  submitButton: (props: CaseFileGenerateSubmitButtonProps) => ReactElement;
  getAvailableDocuments: (
    product: ProductUnion
  ) => Promise<AvailableGeneratableDocument[]> | AvailableGeneratableDocument[];
};

type CaseFileGenerateFormProps = {
  product: ProductUnion;
  componentCfg?: CaseFileGenerateFormConfig;
};

type GeneratableFileOption = AutoCompleteOptionWithValue & {
  value: string;
  disabled: boolean;
  disabledReasons: string[];
};

const defaultCaseFileGenerateCfg: CaseFileGenerateFormConfig = {
  scrollToAnchor: "contractFiles",
  title: "Fertiger Schriftsatz",
  postUrl: "files/generate/",
  helperText: "Art der Datei: z.B. Rechnung alle Gebühren",
  submitButton: ({ isLoading, product, disabled }: CaseFileGenerateSubmitButtonProps) => (
    <ButtonLoading
      sx={{ width: "250px" }}
      isLoading={isLoading}
      disabled={disabled || !product.responsibleLawyer || isLoading}
      type={"submit"}
      color={"primary"}
      variant={"contained"}
    >
      Dokument generieren
    </ButtonLoading>
  ),
  getAvailableDocuments: fetchGeneratableFiles,
};

const CaseFileGenerateForm = ({ product, componentCfg = defaultCaseFileGenerateCfg }: CaseFileGenerateFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: productGeneratableFiles = [], isLoading: isAvailableDocumentsLoading } = useQuery({
    queryKey: ["available_documents", componentCfg.scrollToAnchor, product.productClassName, product.id],
    queryFn: async () => componentCfg.getAvailableDocuments(product),
    enabled: !!product.id
  });

  const onSubmit = async () => {
    const selectedFile = productGeneratableFiles.find((file) => file.documentId === values.fileType);

    if (selectedFile?.isDisabled) {
      return;
    }

    const response = await ApiClient.post(componentCfg.postUrl + product.productClassName + "/" + product.id, {
      body: JSON.stringify({
        documentType: "pdf",
        documentIdentifier: values.fileType,
        sendEmailToCustomer: values.sendEmailToCustomer,
        showInGallery: values.showInGallery,
      }),
    });
    enqueueSnackbar(
      response && response.status === "ok"
        ? "Dokument wurde erfolgreich erstellt"
        : "Dokument konnte nicht erstellt werden",
      {
        variant: response && response.status === "ok" ? "custom" : "error",
        buttonType: "link",
        buttonLink: getCaseLink(product),
        buttonTarget: "_self",
        buttonText: "Zum Fall",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      }
    );
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("media_objects")
    });
  };

  const initialValues = { fileType: "", sendEmailToCustomer: false, showInGallery: false };
  const { values, handleChange, handleSubmit, isLoading } = useForm({
    initialValues,
    onSubmit,
  });

  if (isAvailableDocumentsLoading || productGeneratableFiles.length === 0) {
    return null;
  }

  const IndividualSubmitButton = componentCfg.submitButton;

  const selectedFile = productGeneratableFiles.find((file) => file.documentId === values.fileType);
  const isSelectedFileDisabled = selectedFile?.isDisabled || false;
  const selectedFileDisabledReasons = selectedFile?.disabledReasons.length
    ? selectedFile.disabledReasons
    : ["Dieses Dokument kann aktuell nicht generiert werden."];

  const autoCompleteOptions: GeneratableFileOption[] = productGeneratableFiles.map((file) => {
    return {
      label: file.label,
      value: file.documentId,
      disabled: file.isDisabled,
      disabledReasons: file.disabledReasons,
    };
  });

  return (
    <>
      <Box sx={scrollToAnchor} id={componentCfg.scrollToAnchor} />
      <ContentBox headline={componentCfg.title}>
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={3}
            sx={{
              alignItems: "center",
              justifyContent: "center"
            }}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <LegalbirdAutoComplete<GeneratableFileOption>
                options={autoCompleteOptions}
                value={autoCompleteOptions.find((file) => file.value === formValue(values, "fileType")) || null}
                setValue={(
                  _event: React.SyntheticEvent<Element, Event>,
                  option: AutoCompleteOptionWithValue | null
                ) => {
                  handleChange({
                    target: {
                      name: "fileType",
                      value: option ? option.value : null,
                    },
                  });
                }}
                renderOption={(props, option) => (
                  <Box
                    component={"li"}
                    {...props}
                    key={option.value}
                    sx={{ color: option.disabled ? "grey.500" : "text.primary" }}
                  >
                    {option.label}
                  </Box>
                )}
                textFieldStyles={
                  isSelectedFileDisabled
                    ? {
                        "&& .MuiInputBase-input": {
                          color: "grey.500",
                        },
                      }
                    : {}
                }
                label={"Welche Datei?"}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Stack direction={"row"} sx={{
                justifyContent: "space-evenly"
              }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Für Mandantschaft sichtbar</FormLabel>
                  <RadioGroup row={true} name={"showInGallery"} value={values.showInGallery} onChange={handleChange}>
                    <FormControlLabel
                      disabled={values.sendEmailToCustomer === "true"}
                      value={"true"}
                      control={<Radio />}
                      label={"Ja"}
                    />
                    <FormControlLabel
                      disabled={values.sendEmailToCustomer === "true"}
                      value={"false"}
                      control={<Radio />}
                      label={"Nein"}
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Mandantschaft benachrichtigen</FormLabel>
                  <RadioGroup
                    row={true}
                    name={"sendEmailToCustomer"}
                    value={values.sendEmailToCustomer}
                    onChange={(e) => {
                      handleChange(e);
                      handleChange({ target: { name: "showInGallery", value: "true" } });
                    }}
                  >
                    <FormControlLabel value={"true"} control={<Radio />} label={"Ja"} />
                    <FormControlLabel value={"false"} control={<Radio />} label={"Nein"} />
                  </RadioGroup>
                </FormControl>
              </Stack>
            </Grid>
            {isSelectedFileDisabled && (
              <Grid size={12}>
                <Alert severity={"warning"} sx={{ "& ul": { margin: 0, paddingLeft: "1.25rem", mt: "0.5rem" } }}>
                  <strong>Dokument kann nicht generiert werden:</strong>
                  <ul>
                    {selectedFileDisabledReasons.map((reason, index) => (
                      <li key={`${reason}-${index}`}>{reason}</li>
                    ))}
                  </ul>
                </Alert>
              </Grid>
            )}
            <Grid>
              <PdfGeneratorPreview
                product={product}
                values={{}}
                mediaObjects={[]}
                getParams={() => {}}
                documentIdentifier={values.fileType}
                disabled={!product.responsibleLawyer || isSelectedFileDisabled}
              />
            </Grid>
            <Grid>
              {<IndividualSubmitButton isLoading={isLoading} product={product} disabled={isSelectedFileDisabled} />}
            </Grid>
            <Grid size={12}>
              {!product.responsibleLawyer && (
                <Typography
                  align={"center"}
                  sx={{
                    color: "error.main",
                    marginTop: "1rem"
                  }}>
                  Kein Anwalt am Fall vorhanden. Es können keine Schriftsätze erstellt werden.
                </Typography>
              )}
            </Grid>
          </Grid>
        </form>
      </ContentBox>
    </>
  );
};

export default CaseFileGenerateForm;
